export const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/**
 * Exponential backoff with optional jitter.
 *
 * `attempt` is zero-based, so the first retry uses `baseMs * factor^0 = baseMs`.
 * Jitter, when enabled, multiplies the deterministic delay by a random factor
 * in `[0.75, 1.0)` — full jitter would be cheaper for thundering herds but
 * confuses operators reading dashboards. 75–100% strikes the same balance the
 * AWS Architecture Blog landed on.
 */
export function computeBackoff(attempt, policy) {
    const base = Math.min(policy.baseMs * Math.pow(policy.factor, attempt), policy.maxMs);
    if (!policy.jitter)
        return base;
    return Math.round(base * (0.75 + Math.random() * 0.25));
}
/**
 * Run `send()` up to `policy.maxAttempts` times, sleeping `computeBackoff(...)`
 * between attempts.
 *
 * Retries on:
 *   - any `Response` with `status >= 500`
 *   - any thrown error EXCEPT a caller-initiated `AbortError`
 *
 * Returns the last `Response` (success or final 5xx) so the caller can inspect
 * it. Re-throws when the last attempt threw and was not an abort.
 */
export async function withRetry(send, policy, sleep = defaultSleep) {
    let lastResponse;
    let lastError;
    for (let attempt = 0; attempt < policy.maxAttempts; attempt++) {
        try {
            const res = await send();
            if (res.status < 500)
                return res;
            lastResponse = res;
            lastError = undefined;
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                throw err;
            }
            lastError = err;
            lastResponse = undefined;
        }
        if (attempt < policy.maxAttempts - 1) {
            await sleep(computeBackoff(attempt, policy));
        }
    }
    if (lastResponse)
        return lastResponse;
    throw lastError;
}
//# sourceMappingURL=retry.js.map